import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useContext, useEffect, useState } from 'react';
import { VerticalCardLoader } from 'syf-component-library/ui/atoms';
import { generateUUID } from 'syf-js-utilities';
import { ConfigContext } from '../../context';
import { useAuthorizedUsersGet, useMultiAuthorizedUsersPostMutation } from '../../../../const/syf-api-queries';
import AuthorizedUsers from '../../main-container/components/AuthorizedUsers';
import CardHoldersFooter from '../../main-container/components/CardHoldersFooter';
import { BodyContainer, ModalContentWrapper } from './subcomponents';
import { AddAuthUserInputType, CardholdersScreen } from '../../typings';
import getInputErrorMessage from './helpers/get-input-error-message';
import { initialAddAuthState, initialErrorDetails } from '../../const';
import queryParamsToObject from '../../helpers/query-params-to-object';
import { ANALYTICS_PAYLOAD, INPUT_MISSING_DETAILS, PAGE_NAME } from '../../const/analytics';
import triggerManageCardHoldersAnalytics from '../../helpers/trigger-manage-card-holders-analytics';
import { RESPONSE_STATUS, STATUS_CODE } from '../../const/status-and-error-codes';
import { AddAuthorizedUsers, CardHoldersHeader } from '../../main-container/components';
import displayModalScreen from '../../helpers/display-modal-screen';
import { formatDateToISO } from '../../../../helpers/dateFormatter';
const ManageCardHoldersModalContent = () => {
    const { config, selectedAccountId, handleClose } = useContext(ConfigContext);
    const [inputState, setInputState] = useState(initialAddAuthState);
    const [isUserVerified, setIsUserVerified] = useState(false);
    const [isAddAuthUserSubmitted, setIsAddAuthUserSubmitted] = useState(false);
    const [newAddedAuthorizedUser, setNewAddedAuthorizedUser] = useState();
    const [currentScreen, setCurrentScreen] = useState({
        screen: CardholdersScreen.INITIAL_SCREEN
    });
    const [errorDetails, setErrorDetails] = useState(initialErrorDetails);
    const otpErrorCallbackParams = sessionStorage.getItem('otpErrorCallbackParams');
    const { data: authorizedUsersDataGet, isError: isGetAuthorizedError, isFetching: isGetAuthorizedFetching, isSuccess: isGetAuthorizedSuccess } = useAuthorizedUsersGet(selectedAccountId);
    const { mutateAsync, error: postAuthorizedError, isLoading: isPostAuthorizedLoading } = useMultiAuthorizedUsersPostMutation(config);
    const hasError = isGetAuthorizedError ||
        (isUserVerified && Boolean(postAuthorizedError)) ||
        Boolean(errorDetails.businessErrorCode);
    const showLoader = !hasError && (isGetAuthorizedFetching || isPostAuthorizedLoading);
    const remainingAuthUsersAllowed = authorizedUsersDataGet?.remainingAuthUsersAllowed;
    const handleResponseError = useCallback(async (error) => {
        const responseError = error?.response;
        const responseStatus = error?.response?.status;
        if (responseStatus === RESPONSE_STATUS?.FORBIDDEN && !isUserVerified) {
            setCurrentScreen({
                screen: CardholdersScreen.VERIFY_USER_SCREEN
            });
        }
        else if (responseStatus === RESPONSE_STATUS.BUSINESS_ERROR) {
            const parsedError = await responseError?.json();
            setErrorDetails({
                businessErrorCode: parsedError.businessErrors[0].code,
                businessErrorDescription: parsedError.businessErrors[0].message
            });
        }
        else {
            setErrorDetails(initialErrorDetails);
        }
    }, [isUserVerified]);
    const handleScopeCheck = async () => {
        try {
            await mutateAsync({
                'cipher.accountId': selectedAccountId,
                accountIdType: 'guid',
                authorizedUsers: []
            });
            setIsUserVerified(true);
        }
        catch (error) {
            await handleResponseError(error);
        }
    };
    const clearFields = () => {
        setInputState(initialAddAuthState);
        setIsAddAuthUserSubmitted(false);
        setNewAddedAuthorizedUser(undefined);
        setErrorDetails(initialErrorDetails);
    };
    const changeScreen = (screenProps) => {
        setCurrentScreen(screenProps);
        if (hasError ||
            ![
                CardholdersScreen.ADD_AUTH_USER,
                CardholdersScreen.CONFIRMATION_SCREEN_NEW_USER
            ].includes(screenProps.screen)) {
            clearFields();
        }
    };
    const handleCancel = () => {
        triggerManageCardHoldersAnalytics({
            pageFunction: 'account services',
            pageKind: 'account services'
        });
        handleClose();
        clearFields();
    };
    const updateState = ({ type, value }) => {
        const inputErrorMessage = inputState[type].errorMessage;
        const hasInputAndErrorMessage = Boolean(value && inputErrorMessage);
        setInputState((prev) => {
            return {
                ...prev,
                [type]: {
                    errorMessage: !hasInputAndErrorMessage ? prev[type].errorMessage : '',
                    value
                }
            };
        });
    };
    const handleBlur = ({ type }) => {
        setInputState(prev => {
            const errorMessage = getInputErrorMessage(type, prev[type].value);
            return {
                ...prev,
                [type]: {
                    ...prev[type],
                    value: prev[type].value.trim(),
                    touched: true,
                    errorMessage
                }
            };
        });
    };
    const validateInputFields = () => {
        let valid = true;
        const updateInput = { ...inputState };
        Object.entries(updateInput).forEach(values => {
            const field = values[0];
            const inputErrorMessage = getInputErrorMessage(field, updateInput[field].value);
            updateInput[field].errorMessage = inputErrorMessage;
            if (inputErrorMessage) {
                triggerManageCardHoldersAnalytics({
                    pageName: PAGE_NAME.ERROR,
                    errorMessage: INPUT_MISSING_DETAILS
                });
                valid = false;
            }
        });
        setInputState(updateInput);
        return valid;
    };
    // eslint-disable-next-line sonarjs/cognitive-complexity
    const handleAddAuthUserSubmit = async () => {
        setIsAddAuthUserSubmitted(true);
        if (validateInputFields()) {
            try {
                const payload = {
                    'cipher.accountId': selectedAccountId,
                    accountIdType: 'guid',
                    authorizedUsers: [
                        {
                            operationId: generateUUID(),
                            'cipher.firstName': inputState[AddAuthUserInputType.authorizedUserFirstName].value,
                            'cipher.lastName': inputState[AddAuthUserInputType.authorizedUserLastName].value,
                            'cipher.dateOfBirth': formatDateToISO(inputState[AddAuthUserInputType.authorizedUserBirthDate].value)
                        }
                    ]
                };
                const response = await mutateAsync(payload);
                const responseAuthorizedUsers = response?.authorizedUsers;
                if (responseAuthorizedUsers && responseAuthorizedUsers?.length > 0) {
                    const authorizedUser = responseAuthorizedUsers?.[0];
                    const { statusCode, error: postError } = authorizedUser;
                    const code = postError?.businessErrors?.[0]?.code;
                    const errorMessage = postError?.businessErrors?.[0].description || '';
                    if (statusCode === STATUS_CODE.NEW_USER) {
                        setNewAddedAuthorizedUser({
                            authorizedUsers: payload.authorizedUsers
                        });
                        changeScreen({
                            screen: CardholdersScreen.CONFIRMATION_SCREEN_NEW_USER
                        });
                    }
                    else if (statusCode === STATUS_CODE.EXISTING_USER) {
                        changeScreen({
                            screen: CardholdersScreen.CONFIRMATION_SCREEN_EXISTING_USER
                        });
                    }
                    else if (code && statusCode === STATUS_CODE.BUSINESS_ERROR) {
                        setErrorDetails({
                            businessErrorCode: code,
                            businessErrorDescription: errorMessage
                        });
                    }
                }
            }
            catch (error) {
                await handleResponseError(error);
            }
        }
    };
    useEffect(() => {
        if (hasError) {
            const { businessErrorCode, businessErrorDescription } = errorDetails;
            const analyticsError = businessErrorCode && Boolean(ANALYTICS_PAYLOAD[businessErrorCode])
                ? ANALYTICS_PAYLOAD[businessErrorCode]
                : ANALYTICS_PAYLOAD.api_failure;
            triggerManageCardHoldersAnalytics(analyticsError && businessErrorDescription
                ? {
                    ...analyticsError,
                    errorMessage: businessErrorDescription
                }
                : {
                    pageName: PAGE_NAME.API_FAILURE,
                    errorMessage: 'server error'
                });
            setCurrentScreen({
                screen: CardholdersScreen.ERROR_SCREEN,
                customData: { businessErrorCode }
            });
        }
    }, [errorDetails, hasError]);
    useEffect(() => {
        if (isGetAuthorizedSuccess &&
            remainingAuthUsersAllowed &&
            remainingAuthUsersAllowed !== '0' &&
            !otpErrorCallbackParams) {
            handleScopeCheck();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isGetAuthorizedSuccess,
        remainingAuthUsersAllowed,
        otpErrorCallbackParams
    ]);
    useEffect(() => {
        if (otpErrorCallbackParams) {
            const errorObjectParams = queryParamsToObject(otpErrorCallbackParams);
            let stepUpError = '';
            if ('error' in errorObjectParams) {
                stepUpError = errorObjectParams.error;
            }
            if (stepUpError) {
                triggerManageCardHoldersAnalytics({
                    ...ANALYTICS_PAYLOAD[stepUpError],
                    errorMessage: ANALYTICS_PAYLOAD[stepUpError]?.errorMessage ?? ''
                });
                if (stepUpError === 'user_declined') {
                    setCurrentScreen({ screen: CardholdersScreen.VERIFY_USER_SCREEN });
                }
                if (stepUpError === 'access_denied') {
                    setCurrentScreen({ screen: CardholdersScreen.ACCESS_DENIED_SCREEN });
                }
                else {
                    sessionStorage.removeItem('otpErrorCallbackParams');
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [otpErrorCallbackParams]);
    if (showLoader) {
        return (_jsx("div", { "data-testid": "skeleton-loader", children: _jsx(VerticalCardLoader, {}) }));
    }
    const addAuthorizedUserScreen = currentScreen.screen === CardholdersScreen.ADD_AUTH_USER;
    const isNewUserConfirmationScreen = currentScreen.screen === CardholdersScreen.CONFIRMATION_SCREEN_NEW_USER;
    const showAuthorizedUser = !hasError && !addAuthorizedUserScreen;
    const showHeader = !hasError && !isNewUserConfirmationScreen;
    return (_jsxs(ModalContentWrapper, { id: "modal-content-wrapper", children: [_jsxs(BodyContainer, { children: [showHeader && (_jsx(CardHoldersHeader, { usersLimit: remainingAuthUsersAllowed })), displayModalScreen(currentScreen), showAuthorizedUser && (_jsx(AuthorizedUsers, { authorizedUsers: isNewUserConfirmationScreen
                            ? newAddedAuthorizedUser
                            : authorizedUsersDataGet, currentScreen: currentScreen.screen, remainingAuthUserAllowed: remainingAuthUsersAllowed })), addAuthorizedUserScreen && (_jsx(AddAuthorizedUsers, { ...{
                            authorizedUserInputValuesState: inputState,
                            handleBlur,
                            updateState,
                            isAddAuthUserSubmitted
                        } }))] }), _jsx(CardHoldersFooter, { handleAddAuthUserSubmit: handleAddAuthUserSubmit, handleClose: handleCancel, changeScreen: changeScreen, currentScreen: currentScreen.screen, usersLimit: remainingAuthUsersAllowed })] }));
};
export default ManageCardHoldersModalContent;
